import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { CheckboxItemFlatNode, CheckboxItemNode } from '@tekman/onmat/models';
import { FormElementBase } from '../../adapter/form-element-base.adapter';

@Component({
  selector: 'tekman-checkbox-tree',
  templateUrl: './checkbox-tree.component.html',
  styleUrls: ['./checkbox-tree.component.scss'],
})
export class CheckboxTreeComponent extends FormElementBase implements OnInit, OnChanges, AfterContentInit {
  @Input() dataCheckboxTree: CheckboxItemNode[];
  @Input() set disabled(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
  @Input() isExpandedTree = true;
  @Input() avoidRefresh = false;
  @Output() checklistSelected: EventEmitter<CheckboxItemFlatNode[]> = new EventEmitter<CheckboxItemFlatNode[]>();
  checklistSelection = new SelectionModel<CheckboxItemFlatNode>(true, []);
  treeControl = new FlatTreeControl<CheckboxItemFlatNode>(
    node => node.level,
    node => node.expandable
  );
  treeFlattener: MatTreeFlattener<CheckboxItemNode, CheckboxItemFlatNode, CheckboxItemFlatNode>;
  dataSource: MatTreeFlatDataSource<CheckboxItemNode, CheckboxItemFlatNode, CheckboxItemFlatNode>;
  private _transformer = (node: CheckboxItemNode, level: number) => {
    const flat: CheckboxItemFlatNode = {
      id: node?.id,
      expandable: !!node.children && node.children.length > 0,
      item: node.name === undefined ? node.item : node.name,
      level: level,
      checked: node.checked,
      disabled: node.disabled === undefined ? false : node.disabled,
      columns: node.columns ? node.columns : [],
    };
    if (flat.checked) {
      this.checklistSelection.select(flat);
    } else {
      this.checklistSelection.deselect(flat);
    }
    return flat;
  };
  hasChild = (_: number, node: CheckboxItemFlatNode) => node.expandable;
  getLevel = (node: CheckboxItemFlatNode) => node.level;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.treeFlattener = new MatTreeFlattener(
      this._transformer,
      node => node.level,
      node => node.expandable,
      node => node.children
    );
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    this.dataSource.data = this.dataCheckboxTree;
  }

  ngOnChanges(change: SimpleChanges) {
    this.changeDetectorRef.detectChanges();
    if (change['dataCheckboxTree'] && this.treeControl.dataNodes && change['avoidRefresh'] && !change['avoidRefresh'].currentValue) {
      this.toggleTreeSelectedNode();
    }
  }

  ngAfterContentInit() {
    if (this.isExpandedTree) {
      this.expandTree();
    }
  }

  /** Method to toggle tree if any checkbox is selected */
  private toggleTreeSelectedNode() {
    for (let i = 0; i < this.treeControl.dataNodes.length; i++) {
      if (this.checklistSelection.isSelected(this.treeControl.dataNodes[i])) {
        this.checklistSelection.toggle(this.treeControl.dataNodes[i]);
      }
    }
  }

  private expandTree() {
    for (let i = 0; i < this.treeControl.dataNodes.length; i++) {
      this.treeControl.expand(this.treeControl.dataNodes[i]);
    }
  }

  /** Whether all the descendants of the node are selected. */
  descendantsAllSelected(node: CheckboxItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every(child => {
        return this.checklistSelection.isSelected(child);
      });
    return descAllSelected;
  }

  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: CheckboxItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some(child => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  /** Toggle the item selection. Select/deselect all the descendants node */
  itemSelectionToggle(node: CheckboxItemFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);

    // Force update for the parent
    descendants.forEach(child => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);
    this.emitLeafCheckboxSelection();
  }

  /** Toggle a leaf item selection. Check all the parents to see if they changed */
  leafItemSelectionToggle(node: CheckboxItemFlatNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
    this.emitLeafCheckboxSelection();
  }

  /* Checks all the parents when a leaf node is selected/unselected */
  checkAllParentsSelection(node: CheckboxItemFlatNode): void {
    let parent: CheckboxItemFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  /** Check root node checked state and change it accordingly */
  checkRootNodeSelection(node: CheckboxItemFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every(child => {
        return this.checklistSelection.isSelected(child);
      });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  /* Get the parent node of a node */
  getParentNode(node: CheckboxItemFlatNode): CheckboxItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  /** Emits checked checkbox list on the last level */
  private emitLeafCheckboxSelection(): void {
    this.checklistSelected.emit(this.checklistSelection.selected.filter(item => item.expandable === false));
  }
}
